import React, { useMemo, useState } from "react";
import { Collapse, Button, Alert, Tag, Tooltip } from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleOutlined,
  CloseCircleTwoTone,
  ExclamationCircleOutlined,
  ExclamationCircleTwoTone,
  LoadingOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { TASK_PHASE_ORDER } from "./taskPhases";
import "./TaskPhaseList.css";

function taskOutcome(task) {
  if (task.status === "error") return "error";
  if (task.status === "warning") return "warning";
  if (task.status === "success") return "success";
  if (task.status === "pending") return "active";
  return "waiting";
}

function phaseOutcome(tasks) {
  const outcomes = tasks.map(taskOutcome);
  if (outcomes.includes("error")) return "error";
  if (outcomes.includes("active")) return "active";
  if (outcomes.includes("warning")) return "warning";
  if (outcomes.every((o) => o === "success")) return "success";
  return "waiting";
}

function StatusIcon({ outcome }) {
  let icon;
  if (outcome === "success") {
    icon = <CheckCircleTwoTone twoToneColor="#52c41a" />;
  } else if (outcome === "warning") {
    icon = <ExclamationCircleTwoTone twoToneColor="#faad14" />;
  } else if (outcome === "error") {
    icon = <CloseCircleTwoTone twoToneColor="#ff4d4f" />;
  } else if (outcome === "active") {
    icon = <LoadingOutlined style={{ color: "#7c3aed" }} spin />;
  } else {
    icon = <ClockCircleOutlined style={{ color: "#d9d9d9" }} />;
  }

  return (
    <span key={outcome} className="task-icon">
      {icon}
    </span>
  );
}

function PhaseIssues({ tasks }) {
  const errors = tasks.filter((task) => taskOutcome(task) === "error").length;
  const warnings = tasks.filter(
    (task) => taskOutcome(task) === "warning"
  ).length;
  if (errors === 0 && warnings === 0) return null;

  const isBlocking = errors > 0;
  const count = isBlocking ? errors : warnings;
  const plural = count > 1 ? "steps" : "step";

  return (
    <Tooltip
      title={
        isBlocking
          ? `${count} ${plural} failed and stopped the setup`
          : `${count} ${plural} failed, the setup continues`
      }
    >
      <Tag
        color={isBlocking ? "error" : "warning"}
        icon={
          isBlocking ? <CloseCircleOutlined /> : <ExclamationCircleOutlined />
        }
        style={{ margin: 0 }}
      >
        {count}
      </Tag>
    </Tooltip>
  );
}

function TaskRow({ task }) {
  const [showDetail, setShowDetail] = useState(false);
  const outcome = taskOutcome(task);
  const hasDetail =
    (outcome === "warning" || outcome === "error") && task.messages;

  return (
    <div className="task-row">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <StatusIcon outcome={outcome} />
        <span
          style={{
            fontSize: 13.5,
            color: outcome === "waiting" ? "#bfbfbf" : "#374151",
          }}
        >
          {task.name}
        </span>
        {task.summary && (
          <span
            style={{
              fontSize: 12,
              color: "#9ca3af",
              fontStyle: outcome === "success" ? "normal" : "italic",
            }}
          >
            {task.summary}
          </span>
        )}
        {outcome === "warning" && !task.summary && (
          <span style={{ fontSize: 12, color: "#d48806" }}>
            Failed, the setup continues
          </span>
        )}
        <span style={{ flex: 1 }} />
        {hasDetail && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto" }}
            onClick={() => setShowDetail((current) => !current)}
          >
            {showDetail
              ? "Hide"
              : outcome === "error"
              ? "Show error"
              : "Show details"}
          </Button>
        )}
      </div>
      {hasDetail && showDetail && (
        <Alert
          style={{
            marginTop: 8,
            marginLeft: 26,
            fontFamily: "monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
          type={outcome === "error" ? "error" : "warning"}
          message={task.messages}
        />
      )}
    </div>
  );
}

export default function TaskPhaseList({ tasks, expectedTasks }) {
  const [manuallyOpened, setManuallyOpened] = useState([]);

  const groups = useMemo(() => {
    const byPhase = {};

    if (expectedTasks && expectedTasks.length > 0) {
      const byName = {};
      for (const task of tasks) {
        byName[task.name] = task;
      }
      for (const expected of expectedTasks) {
        const phase = expected.phase || "Other";
        if (!byPhase[phase]) {
          byPhase[phase] = [];
        }
        byPhase[phase].push(byName[expected.name] || { ...expected, phase });
      }
    } else {
      for (const task of tasks) {
        const phase = task.phase || "Other";
        if (!byPhase[phase]) {
          byPhase[phase] = [];
        }
        byPhase[phase].push(task);
      }
    }

    return [...TASK_PHASE_ORDER, "Other"]
      .filter((phase) => byPhase[phase])
      .map((phase) => ({ phase, tasks: byPhase[phase] }));
  }, [tasks, expectedTasks]);

  const activeKeys = groups
    .filter(({ phase, tasks: phaseTasks }) => {
      const outcome = phaseOutcome(phaseTasks);
      return outcome === "active" || manuallyOpened.includes(phase);
    })
    .map(({ phase }) => phase);

  function handleChange(keys) {
    const activePhases = groups
      .filter(({ tasks: phaseTasks }) => phaseOutcome(phaseTasks) === "active")
      .map(({ phase }) => phase);
    setManuallyOpened(keys.filter((key) => !activePhases.includes(key)));
  }

  return (
    <Collapse
      className="task-phase-collapse"
      activeKey={activeKeys}
      onChange={handleChange}
      bordered={false}
    >
      {groups.map(({ phase, tasks: phaseTasks }) => {
        const outcome = phaseOutcome(phaseTasks);
        const doneCount = phaseTasks.filter((task) => {
          const taskStatus = taskOutcome(task);
          return taskStatus !== "waiting" && taskStatus !== "active";
        }).length;

        return (
          <Collapse.Panel
            key={phase}
            className={
              outcome === "error" ? "task-phase-panel--error" : undefined
            }
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <strong style={{ fontSize: 13.5 }}>{phase}</strong>
                <PhaseIssues tasks={phaseTasks} />
              </div>
            }
            extra={
              <span
                style={{
                  fontSize: 12,
                  color: "#9ca3af",
                  fontFamily: "monospace",
                }}
              >
                {doneCount}/{phaseTasks.length}
              </span>
            }
          >
            {phaseTasks.map((task) => (
              <TaskRow key={task.name} task={task} />
            ))}
          </Collapse.Panel>
        );
      })}
    </Collapse>
  );
}
